import {
  Component,
  OnInit,
  ViewChild,
  ElementRef,
  HostListener,
  OnDestroy,
} from '@angular/core';
import { CMSContentService } from '../essentials/cms/content/cms-content.service';
import { AzureSearchService } from '../../app/essentials/azure/search/search.service';

import { Router, ActivatedRoute } from '@angular/router';
import { Subscription, Observable, fromEvent } from 'rxjs';
import { debounceTime, switchMap, take } from 'rxjs/operators';

import { SlugPipe } from '../essentials/modules/pipe/slug/slug.pipe';
import {
  NewsConfigsData,
  NewsHighlightData,
  HighlightBlock,
} from '../shared/models/news-highlight.model';

@Component({
  selector: 'fcp-news-highlight',
  templateUrl: './news-highlight.component.html',
  styleUrls: ['./news-highlight.component.scss'],
})
export class NewsHighlightComponent implements OnInit, OnDestroy {
  @ViewChild('newsSection', { static: false }) newsSection: ElementRef;
  @ViewChild('bannerMenu', { static: true }) bannerMenu: ElementRef;

  public isDeviceDesktop: boolean;
  public isDetailScreen: boolean;
  public showResults: boolean;
  public firstLoad: boolean;
  public loading: boolean;
  public selectedCategory: string;
  public searchTerm: string;
  public page: number;
  public news: Array<any>;
  public suggestions: Array<any>;
  public newsHighlight: NewsHighlightData;

  private heightHeader: number;
  private skip: number;
  private top: number;
  private unfilteredList: Array<NewsHighlightData>;
  private resizeObservable$: Observable<Event>;
  private resizeSubscription$: Subscription;
  private newsConfigs: NewsConfigsData;

  constructor(
    private cmsContent: CMSContentService,
    private azureService: AzureSearchService,
    private router: Router,
    private route: ActivatedRoute,
    private slugPipe: SlugPipe
  ) {
    this.initVariables();
    this.loadContent();
  }

  public ngOnInit(): void {
    this.resizeObservable$ = fromEvent(window, 'resize');
    this.resizeSubscription$ = this.resizeObservable$
      .pipe(debounceTime(300), take(1))
      .subscribe(() => this.checkDevice());
  }

  public ngOnDestroy(): void {
    this.resizeSubscription$.unsubscribe();
  }

  @HostListener('window:scroll', ['$event'])
  public onWindowScroll(from?: any) {
    let footerPosition = 0;
    const bannerElement = document.getElementsByClassName(
      'section-news-highlight__banner'
    )[0];
    if (bannerElement) {
      footerPosition = bannerElement.clientHeight;
    }
    if (window.pageYOffset > footerPosition) {
      this.bannerMenu.nativeElement.classList.add('position-fixed');
    } else {
      this.bannerMenu.nativeElement.classList.remove('position-fixed');
    }
  }

  public goToCategory(category: string): void {
    window.location.href = `noticias/${this.slugPipe.transform(category)}`;
  }

  public actionSearchTerm($event, search?: boolean) {
    const searchTerm = this.searchTerm;
    const key = $event.key ? $event.key.toLowerCase() : null;
    const hitEnterOrSearchButton = key === 'enter' || search;

    if (!searchTerm && hitEnterOrSearchButton) {
      window.location.href = 'noticias';
      return;
    }

    if (searchTerm && searchTerm.length > 0) {
      if (hitEnterOrSearchButton) {
        search = true;
        this.firstLoad = false;
        window.location.href = `noticias?q=${searchTerm}`;
      } else if (/[a-z]/.test(key)) {
        this.getSuggestions(searchTerm);
      }
    }
  }

  public openModal(modal): void {
    if (modal) {
      this.cmsContent.openModal(modal);
    }
  }

  public goTo(slug: string): void {
    this.router.navigateByUrl(`${slug}`);
  }

  public changePage(page) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { p: page },
      queryParamsHandling: 'merge',
    });
  }

  private initVariables(): void {
    this.showResults = false;
    this.firstLoad = false;
    this.loading = false;
    this.selectedCategory = '';
    this.heightHeader = 77;
    this.page = 1;
    this.top = 12;
    this.skip = 0;
    this.news = [];
    this.suggestions = [];
    this.unfilteredList = [];
    this.newsHighlight = new NewsHighlightData();
    this.isDetailScreen = this.checkIfIsDetailScreen();
  }

  private loadContent(): void {
    this.route.queryParams.pipe(take(1)).subscribe((params) => {
      if (location.pathname.indexOf('/noticias') >= 0) {
        if (params['p']) {
          this.page = params['p'];
        }
        if (params['q']) {
          this.searchTerm = params['q'];
          this.getContent(params['q']);
        } else {
          this.getContent('');
        }
      }
    });
  }

  private selectCategory(category: string) {
    this.isDetailScreen = false;
    const myNews = this.filterNews(this.unfilteredList);
    this.news = myNews.filter((article) => {
      if (this.slugPipe.transform(article.category) === category) {
        this.selectedCategory = article.category;
        return true;
      }
      return false;
    });

    if (this.news.length === 0) {
      this.selectedCategory = `As notícias de ${category} ainda não foram cadastradas.`;
    }

    const elementPositionTop =
      this.newsSection?.nativeElement['offsetTop'] - this.heightHeader;
    window.scrollTo(0, elementPositionTop);
  }

  private getSuggestions(term) {
    this.showResults = false;
    if (term !== '*') {
      this.azureService
        .getSuggestions(term, 'Notícias')
        .subscribe((results) => {
          this.suggestions = results;
        });
    }
  }

  private search(term, termAfter?: string) {
    this.suggestions = [];
    this.loading = true;

    if (this.page === 1) {
      this.skip = 0;
    } else {
      this.skip = (this.page - 1) * this.top;
    }

    this.azureService
      .getCognitiveSearch(term, 'Notícias')
      .pipe(take(1))
      .subscribe((results) => {
        this.unfilteredList = results.value;
        this.news = this.filterNews(results.value);

        if (term === '*') {
          this.buildHighlightsNews();
          this.newsHighlight.menu = this.buildMenuCategory(this.news);
          this.checkDevice();
          const category = this.route.snapshot.paramMap.get('category');
          if (!this.isDetailScreen && category) {
            this.selectCategory(category);
          }
          if (this.isDetailScreen) {
            window.scrollTo(0, 500);
          }

          if (termAfter && termAfter !== '') {
            this.search(termAfter);
          } else {
            this.showResults = true;
            this.firstLoad = true;
            this.loading = false;
          }
        } else {
          this.showResults = true;
          this.firstLoad = true;
          this.loading = false;
        }
      });
  }

  private checkDevice() {
    if (window.innerWidth < 769) {
      this.isDeviceDesktop = false;
      this.addNewsFromTheFloatingMenu();
      return;
    }
    this.isDeviceDesktop = true;
    this.removeNewsFromTheFloatingMenu();
  }

  private addNewsFromTheFloatingMenu() {
    if (!this.haveNewsOnTheFloatingMenu()) {
      this.newsHighlight.menu.push({
        title: 'IR PARA PUBLICAÇÕES',
        url: '/publicacoes',
      });
    }
  }

  private haveNewsOnTheFloatingMenu() {
    return this.newsHighlight.menu.find(
      (item) => item.title === 'IR PARA PUBLICAÇÕES'
    );
  }

  private removeNewsFromTheFloatingMenu() {
    if (this.haveNewsOnTheFloatingMenu()) {
      this.newsHighlight.menu.pop();
    }
  }

  private getContent(termAfter?: string): void {
    this.cmsContent
      .getConfigNews()
      .pipe(take(1))
      .subscribe((newsConfigs: NewsConfigsData) => {
        this.newsConfigs =
          newsConfigs && newsConfigs[0] ? newsConfigs[0] : null;
        this.search('*', termAfter);
      });
  }

  private checkIfIsDetailScreen(): boolean {
    return (
      this.isDefined(this.route.snapshot.paramMap.get('slug')) ||
      this.isDefined(this.route.snapshot.paramMap.get('title'))
    );
  }

  private isDefined(value: any): boolean {
    return value !== null && value !== undefined;
  }

  private filterNews(news: any): any[] {
    return news
      .filter(
        (newItem) =>
          this.newsConfigs.banner !== newItem.id &&
          this.newsConfigs.blocks.bigger !== newItem.id &&
          this.newsConfigs.blocks.firstSmall !== newItem.id &&
          this.newsConfigs.blocks.secoundSmall !== newItem.id
      )
      .map((item) => {
        const newItem = item;
        newItem.url = '/' + item.url;
        return newItem;
      });
  }

  private buildMenuCategory(list: Array<any>): Array<{ title: string }> {
    return list
      .map((item) => item.category)
      .filter((item, index, array) => array.indexOf(item) === index)
      .map((title) => {
        return { title };
      });
  }

  private buildHighlightsNews(): void {
    this.cmsContent
      .getNewsById(this.newsConfigs.banner)
      .pipe(
        switchMap((banner) => {
          this.newsHighlight.subtitle = banner.titulo;
          this.newsHighlight.description = banner.subtitulo;
          this.newsHighlight.image = banner.imagemDestaque[0].url;
          this.newsHighlight.slug = banner.slug;
          return this.cmsContent.getNewsById(this.newsConfigs.blocks.bigger);
        }),
        switchMap((bigger) => {
          const block: any = new HighlightBlock(bigger);
          this.newsHighlight.blocks.bigger = block;
          return this.cmsContent.getNewsById(
            this.newsConfigs.blocks.firstSmall
          );
        }),
        switchMap((firstSmall) => {
          const block: any = new HighlightBlock(firstSmall);
          this.newsHighlight.blocks.firstSmall = block;
          return this.cmsContent.getNewsById(
            this.newsConfigs.blocks.secoundSmall
          );
        })
      )
      .subscribe((secoundSmall) => {
        const block: any = new HighlightBlock(secoundSmall);
        this.newsHighlight.blocks.secoundSmall = block;
      });
  }
}
